import React from "react"
import { twMerge } from "tailwind-merge"

import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily, getParagraphFontWeight } from "../utils/fontUtils"
import { getWysiwygScopeId, getWysiwygScopeSelector } from "../utils/wysiwygScopeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const GenericSection = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "text-image"
  const fullWidth = settings.fullWidth || false
  const showButton = settings.showButton || false

  // Get color scheme based on color mode (white / light / dark)
  const colorScheme = getSectionColorScheme(settings, theme)
  const sectionBackgroundColor = colorScheme.sectionBackground
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor
  const wysiwygScope = getWysiwygScopeSelector(section.id, "generic")
  const wysiwygScopeId = getWysiwygScopeId(section.id, "generic")

  const renderContent = () => {
    const contentElement = (
      <div>
        {section.body && (
          <>
            <style>{`
              ${wysiwygScope} {
                text-align: inherit !important;
                word-break: normal !important;
                overflow-wrap: break-word !important;
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} p,
              ${wysiwygScope} li,
              ${wysiwygScope} span,
              ${wysiwygScope} div:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6),
              ${wysiwygScope} *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6):not(strong):not(b):not(em):not(i):not(u) {
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              ${wysiwygScope} .ql-align-right {
                text-align: right !important;
              }
              ${wysiwygScope} .ql-align-center {
                text-align: center !important;
              }
              ${wysiwygScope} .ql-align-justify {
                text-align: justify !important;
              }
              ${wysiwygScope} .ql-align-left {
                text-align: left !important;
              }
              ${wysiwygScope} a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:first-child {
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:last-child {
                margin-bottom: 0 !important;
              }
              ${wysiwygScope} strong,
              ${wysiwygScope} b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                color: inherit !important;
              }
              ${wysiwygScope} em,
              ${wysiwygScope} i {
                font-style: italic !important;
              }
              ${wysiwygScope} u {
                text-decoration: underline !important;
              }
              ${wysiwygScope} h1 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 56px !important;
                line-height: 64px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h2 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h3 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 28px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h4 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 20px !important;
                line-height: 24px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h5 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${bodyColor} !important;
              }
              @media (max-width: 768px) {
                ${wysiwygScope} h1 {
                  font-size: 26px !important;
                  line-height: 32px !important;
                  overflow-wrap: normal !important;
                  word-break: normal !important;
                  hyphens: none !important;
                }
                ${wysiwygScope} h2 {
                  font-size: 28px !important;
                  line-height: 32px !important;
                }
                ${wysiwygScope} h3 {
                  font-size: 24px !important;
                  line-height: 28px !important;
                }
                ${wysiwygScope} h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
            <div
              id={wysiwygScopeId}
              className="generic-section-content prose prose-lg max-w-none" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontWeight: getParagraphFontWeight(theme?.fonts?.paragraph, theme?.fonts?.paragraphRegularWeight),
                color: bodyColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
        {showButton && settings.buttonText && (
          <div className="mt-6">
            <a
              href={settings.buttonLink || "#"}
              className={`inline-block rounded px-6 py-3 text-sm font-medium transition-colors ${
                settings.buttonStyle === "primary"
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : settings.buttonStyle === "secondary"
                  ? "border border-teal-600 text-teal-600 hover:bg-teal-50"
                  : "text-teal-600 underline hover:text-teal-700"
              }`}>
              {settings.buttonText}
            </a>
          </div>
        )}
      </div>
    )

    const imageElement = section.imageUrl && (
      <div>
        <img
          src={section.imageUrl}
          alt={section.headline || section.layout}
          className="h-auto w-full rounded-lg shadow-lg"
        />
      </div>
    )

    // Highlights renderer for highlights_2 and highlights_3 layouts
    const highlights = settings.highlights || []
    const renderHighlights = (count) => (
      <div className={`grid gap-8 ${count === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
        {highlights.slice(0, count).map((highlight, index) => (
          <div key={index} className="flex flex-col gap-4">
            {highlight.imageUrl && (
              <img
                src={highlight.imageUrl}
                alt={highlight.title || `Highlight ${index + 1}`}
                className="h-auto w-full rounded-lg object-cover"
              />
            )}
            {highlight.title && (
              <h3
                className="text-xl font-bold"
                style={{
                  fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: headingColor
                }}>
                {highlight.title}
              </h3>
            )}
            {highlight.description && (
              <div
                className="generic-section-content prose prose-lg max-w-none" // eslint-disable-line tailwindcss/no-custom-classname
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: bodyColor
                }}
                dangerouslySetInnerHTML={{ __html: highlight.description }}
              />
            )}
          </div>
        ))}
      </div>
    )

    switch (layout) {
      // Legacy layout names
      case "text-only":
        return contentElement
      case "image-text":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {imageElement}
            {contentElement}
          </div>
        )
      case "centered":
        return (
          <div className="mx-auto max-w-3xl text-center">
            {contentElement}
            {imageElement}
          </div>
        )
      // New layout names from ContentEditor
      case "content_centered":
        return (
          <div className="mx-auto max-w-3xl text-center">
            {contentElement}
            {imageElement}
          </div>
        )
      case "text_centered":
        return <div className="mx-auto max-w-3xl text-center">{contentElement}</div>
      case "image_right":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {contentElement}
            {imageElement}
          </div>
        )
      case "image_left":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {imageElement}
            {contentElement}
          </div>
        )
      case "half_image_right":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {contentElement}
            <div className="relative">{imageElement}</div>
          </div>
        )
      case "half_image_left":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div className="relative">{imageElement}</div>
            {contentElement}
          </div>
        )
      case "highlights_2":
        return (
          <div className="flex flex-col gap-10">
            {contentElement}
            {renderHighlights(2)}
          </div>
        )
      case "highlights_3":
        return (
          <div className="flex flex-col gap-10">
            {contentElement}
            {renderHighlights(3)}
          </div>
        )
      case "bento_left": {
        const images = settings.images || []
        return (
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              {images.map((img, i) => (
                <img key={i} src={img} alt={`Image ${i + 1}`} className="h-auto w-full rounded-lg object-cover" />
              ))}
            </div>
            {contentElement}
          </div>
        )
      }
      case "overlap_double_image": {
        const overlapImages = settings.images || []
        return (
          <div className="flex flex-col gap-10">
            {contentElement}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {overlapImages.map((img, i) => (
                <img key={i} src={img} alt={`Image ${i + 1}`} className="h-auto w-full rounded-lg object-cover" />
              ))}
            </div>
          </div>
        )
      }
      case "text-image":
      default:
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {contentElement}
            {imageElement}
          </div>
        )
    }
  }

  return (
    <section
      className={twMerge("px-4 py-16", fullWidth ? "px-0" : "", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: sectionBackgroundColor }}>
      <div className={twMerge("mx-auto", fullWidth ? "max-w-none px-4" : "max-w-4xl")}>
        {section.headline && (
          <h2
            className={twMerge(
              "mb-6 text-2xl font-bold",
              ["centered", "content_centered", "text_centered"].includes(layout) ? "text-center" : "text-left"
            )}
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor,
              wordBreak: "normal",
              overflowWrap: "normal",
              hyphens: "none"
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className={twMerge(
              "mb-8 text-base",
              ["centered", "content_centered", "text_centered"].includes(layout) ? "text-center" : "text-left"
            )}
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: bodyColor,
              wordBreak: "normal",
              overflowWrap: "normal"
            }}>
            {section.subheadline}
          </p>
        )}

        {renderContent()}
      </div>
    </section>
  )
}

export default GenericSection
